/* @flow */
import {Observable} from 'rxjs';
import {replace, LOCATION_CHANGE} from 'react-router-redux';
import {Map, fromJS} from 'immutable';
import {combineEpics} from 'redux-observable';
import {
  filter,
  fromPairs,
  map,
  path as keyPath,
  prop,
  compose,
  pipe,
  uniq,
  tap,
} from 'ramda';
import moment from 'moment';
import {saveTextToFile, listToOrderedMap, querystring, parseServerErrorMessage} from 'trustar/utils';
import {IO} from 'trustar/fp';
import {INCIDENT_REPORT_TYPE, INDICATOR_TYPE, Note, type RawNoteResponse} from 'trustar/constellation/types.js';
import {removeNodes, calculateFiltered} from 'trustar/constellation/graph-utils.js';
import createDebugger from 'debug';
import {convertToRaw, convertFromRaw, EditorState} from 'draft-js';
import {warn, error} from 'modules/alerts/actions';
import {FETCH_ANALYSIS_SUCCESS} from 'modules/analysis/action-types';
import {
  REMOVE_SUCCESS,
  STORE_SUCCESS as STORE_REPORT_SUCCESS,
  SET_CURRENT_ID as SET_CURRENT_REPORT_ID
} from 'modules/reports/action-types';
import {
  SAVE_WHITELIST_ENTRY_SUCCESS,
  DELETE_WHITELIST_ENTRY_SUCCESS,
  SAVE_WHITELIST_ENTRIES_SUCCESS
} from 'modules/company-whitelist/action-types';

const debug = createDebugger('app:modules:constellation:epics'); // eslint-disable-line no-unused-vars

import {
  UNDO,
  REDO,
  RESET,
  UNDO_SUCCESS,
  REDO_SUCCESS,
  RESET_SUCCESS,
  FETCH,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  FETCH_EXPLOSION,
  FETCH_EXPLOSION_SUCCESS,
  FETCH_EXPLOSION_FAILURE,
  DELETE_NODES,
  DELETE_NODES_SUCCESS,
  SET_FILTERS,
  SET_FILTERS_SUCCESS,
  EXPORT_INDICATORS,
  CACHE_GRAPH_SIMULATION,
  CACHE_GRAPH_SIMULATION_SUCCESS,
  INVALIDATE_GRAPH_CACHE,
  SAVE_NOTE,
  SAVE_NOTE_SUCCESS,
  SAVE_NOTE_FAILURE,
  UPDATE_NOTE,
  UPDATE_NOTE_SUCCESS,
  UPDATE_NOTE_FAILURE,
  DELETE_NOTE,
  DELETE_NOTE_SUCCESS,
  DELETE_NOTE_FAILURE,
  PROCESS_ANNOTATIONS_SUCCESS,
  PROCESS_ANNOTATIONS_FAILURE,
  SET_ACTIVE_NOTE,
  REPLACE_SELECTION,
  NODE_CLICKED,
  GO_TO_BREADCRUMB,
  RESET_HIGHLIGHT_NODE,
} from './action-types.js';
import {graphEndpoint, queryRecordParams, queryIndicatorParams} from './constants';
import {fetch} from './actions.js';
import * as getters from './getters';
import {ADD_BREADCRUMB} from './action-types';

// TODO: Ugly without being properly composed (repeated id). refactor later
const url = (params) => {
  if (params.type === 'INDICATOR') {
    return compose(querystring({...queryIndicatorParams, ...params}), graphEndpoint)(params.value);
  } else {
    return compose(querystring({...queryRecordParams, ...params}), graphEndpoint)(params.id);
  }
};

/* Log possible errors and keep Rx stream alive */
const handleError = (error, stream) => {
  const message = 'Error in Constellation Epics:';
  console.warn(message);
  console.error(error);
  // TODO_LOG
  // Raven.captureException(new Error(message), { extra: { error }});
  return stream;
};

// We want to be sure that the "index" key is not present because it will be
// overwritten by the utterly silly d3-force. ʕつಠᴥಠʔつ ︵ ┻━┻
// Wrapped in IO just to guard against this loci producing any unforseen errors
// and breaking the epic.
const normalizeOsintNodes = graph => IO.of(graph)
  .map(graph => {
    const nodes = graph.nodes.map(x => {
      if (x.index) {
        x.esIndex = x.index;
        delete x.index;
      }
      return x;
    });

    return {...graph, nodes};
  })
  .foldOr(() => graph);

/**
 * NOTE: If x.payload is meant to fetch an indicator based graph then we need to
 * re-construct its id after the fetch.
 *
 * If the underlying report is not fully processed by the server Station will return a 404.
 */
const fetchEpic = (action$, store, {ajax}) =>
  action$.ofType(FETCH)
    .switchMap(x => {
      const id = x.payload.id || (x.payload.indicatorType + '|' + x.payload.value);

      return ajax.get(url(x.payload))
        .map(prop('response'))
        .map(normalizeOsintNodes)
        .mergeMap(response => {

          const action = [{
            type: FETCH_SUCCESS,
            payload: {
              id, // See NOTE
              data: response,
              type: x.payload.type,
              indicatorType: x.payload.indicatorType,
              value: x.payload.value,
            },
          }];

          const {reports, iocs} = response.nodes.reduce((counts, {nodeType}) => {
            if (nodeType === 'RECORD') counts.reports++;
            if (nodeType === 'INDICATOR') counts.iocs++;
            return counts;
          }, {reports: 0, iocs: 0});

          if (reports <= 1 && x.payload.id) {
            if (iocs === 0) {
              action.push(warn(`
                NOTE: This report has no correlations or extracted indicators.
              `));
            } else {
              action.push(warn(`
                NOTE: This report has no correlations to other reports.
              `));
            }
          }

          return Observable.from(action);

        })
        .catch(err => Observable.of({
          type: FETCH_FAILURE,
          error: true,
          payload: {id, err},
          meta: {
            alert: {
              type: prop('status', err) === 404 ? 'info' : 'error', // see NOTE
              text: parseServerErrorMessage(err)
            }
          }
        }));
    });

export const recordFetchById = (id: string, ajax: Function, originId: ?string) => {
  const url = querystring({...queryRecordParams, id, originId}, graphEndpoint());
  return ajax.get(url);
};

export const indicatorFetchById = (id: string, value: string, indicatorType: string, ajax: Function, originId: ?string) => {
  const url = querystring({...queryIndicatorParams, id, value, indicatorType, originId}, graphEndpoint());
  return ajax.get(url);
};

const fetchExplosionEpic = (action$, store, {ajax}) =>
  action$.ofType(FETCH_EXPLOSION)
    .map(prop('payload'))
    .mergeMap(node => Observable.defer(() => {
      const {id, initialId, type, value, nodeType, indicatorType} = node;
      if (!id) {
        throw new Error('No nodeId provided');
      }

      if (type !== INCIDENT_REPORT_TYPE && nodeType !== INDICATOR_TYPE) {
        throw new Error(`You are trying to fetch constellation for unsupported type "${node.type}"`);
      }

      if (!initialId) {
        throw new Error('You are trying to fetch constellation without a report ID');
      }

      const graphId = getters.currentGraphId(store.getState());

      const ajaxByType = nodeType === INDICATOR_TYPE ? indicatorFetchById(initialId, value, indicatorType, ajax, graphId)
        : recordFetchById(initialId, ajax, graphId);

      return ajaxByType
        .map(prop('response'))
        .map(normalizeOsintNodes)
        .map(response => ({
          type: FETCH_EXPLOSION_SUCCESS,
          payload: {
            id: graphId,
            data: response,
          },
        }))
        .catch(err => Observable.of({ // Catch fetch errors
          type: FETCH_EXPLOSION_FAILURE,
          error: true,
          payload: err,
          meta: {
            alert: {
              type: 'error',
              text: `There was an error fetching additional relations for report with id "${initialId}"`,
            },
          },
        }));
    })
      .catch(err => Observable.of({ // Catch validation errors thrown above
        type: FETCH_EXPLOSION_FAILURE,
        error: true,
        payload: err,
      })));


/**
 * Given a Set of nodeIds return all the nodes and edges in the current
 * graph that are either not in the Set or not connected to any id in the
 * set (in the case of edges).
 *
 * Update 2016-10-13: Deletion now also removes orphaned nodes. Meaning any
 * node which no longer has any edges connected to it.
 *
 * NOTE: This sort of logic is normally reserved for stores. However, since we
 * want to verify the new state and dispatch other side effects if it's invalid
 * it made more sense to do it here. This issue could likely be elegantly
 * resolved with something like redux-observable. Something to think about in
 * the future if we implement our own.
 */
const deleteCurrentSelectionEpic = (action$, store, {ajax}) =>
  action$.ofType(DELETE_NODES)
    .mergeMap(() => {
      const state = store.getState();
      const nodeIds = getters.currentNodeIds(state);
      const graph = getters.currentGraphWithEnclaves(state);

      const filtered = calculateFiltered(graph, graph.getIn(['filters', 'inactiveFilters']));
      const newGraph = removeNodes(graph, filtered.merge(nodeIds));

      if (!newGraph.getIn(['data', 'nodes']).size) {
        return Observable.of(
          error('Cannot delete selection because no nodes would remain in this graph.')
        );
      }

      const id = getters.currentGraphId(state);
      return Observable.of(
        {type: DELETE_NODES_SUCCESS, payload: {id, data: nodeIds}}
      );

    }).catch(handleError);

const toggleActiveFilterEpic = (action$, store, {ajax}) =>
  action$.ofType(SET_FILTERS)
    .mergeMap(({payload: {filters: filterPayload, type, system, enclaveId, tag}}) => {
      const filterText = filterPayload ? filterPayload.filterText : null;
      const state = store.getState();
      const graph = getters.currentGraphWithEnclaves(state);
      const filters = getters.currentGraphFilters(state)
        .set('filterText', filterText ? filterText : '');

      let newFilters = filters;
      if (enclaveId) {
        newFilters = filters.updateIn(['inactiveFilters'], (v) => {
          return v.has(enclaveId) ? v.delete(enclaveId) : v.set(enclaveId, {type, enclaveId});
        });
      } else if (system) {
        newFilters = filters.updateIn(['inactiveFilters'], (v) => {
          return v.has(system) ? v.delete(system) : v.set(system, {type, system});
        });
      } else if (type) {
        newFilters = filters.updateIn(['inactiveFilters'], (v) => {
          return v.has(type) ? v.delete(type) : v.set(type, {type});
        });
      } else if (tag) {
        newFilters = filters.updateIn(['inactiveFilters'], (v) => {
          return v.has(tag) ? v.delete(tag) : v.set(tag, {tag});
        });
      }

      const filtered = calculateFiltered(graph, newFilters.get('inactiveFilters'));
      const newGraph = removeNodes(graph, filtered.merge(graph.get('deletedNodes')));

      if (!newGraph.getIn(['data', 'nodes']).size) {
        return Observable.of(
          error('Cannot delete selection because no nodes would remain in this graph.'),
        );
      } else {
        return Observable.of({
          type: SET_FILTERS_SUCCESS,
          payload: {
            id: getters.currentGraphId(state),
            filters: newFilters,
          }
        });
      }
    }).catch(handleError);

/**
 * Store the coordinates of all the nodes in the graph cache so that we do not
 * need to resimulate if the user comes back to this report.
 *
 * NOTE: I'm not sure checking the simulation is necessary, but it was left over
 * here so for now i've kept it
 *
 * NOTE: If the graph has not yet settled the the nodes won't have an fx, or fy.
 * In this case default to the current x,y.
 */
const cacheGraphSimulationEpic = (action$, store, {ajax}) =>
  action$.ofType(CACHE_GRAPH_SIMULATION)
    .filter(({payload: simulation}) => simulation && typeof simulation.nodes === 'function') // See NOTE
    .mergeMap(({payload: simulation}) => {
      const id = getters.currentGraphId(store.getState());

      // NOTE: vx,vy are also present, but are they relevant for our purposes?
      // So far it seems not.
      const data = {
        nodes: simulation.nodes().map(({id, x, y, fx, fy, index}) => ({
          id,
          x,
          y,
          fx: fx || x,
          fy: fy || y,
          index,
        })),
      };

      return Observable.of({type: CACHE_GRAPH_SIMULATION_SUCCESS, payload: {id, data}});
    })
    .catch(handleError);

/**
 * Redirect back to browse after report has been removed
 */
const redirectOnRemoveEpic = (action$, store) =>
  action$.ofType(REMOVE_SUCCESS)
    .filter(() => {
      const routerState = store.getState().get('router');
      const {locationBeforeTransitions: {pathname}} = routerState;

      const pathHasReportId = pathname
        .replace('constellation/reports', '')
        .replace(/\//g, '');

      return pathname.includes('constellation/reports') && !pathHasReportId;
    })
    .map(() => replace('/browse'));

const undoEpic = (action$, store, {ajax}) =>
  action$.ofType(UNDO)
    .mergeMap(() => {
      const id = getters.currentGraphId(store.getState());
      return Observable.of({type: UNDO_SUCCESS, payload: {id}});
    }).catch(handleError);

const redoEpic = (action$, store, {ajax}) =>
  action$.ofType(REDO)
    .mergeMap(() => {
      const id = getters.currentGraphId(store.getState());
      return Observable.of({type: REDO_SUCCESS, payload: {id}});
    }).catch(handleError);

const resetEpic = (action$, store, {ajax}) =>
  action$.ofType(RESET)
    .mergeMap(() => {
      const id = getters.currentGraphId(store.getState());
      return Observable.of({type: RESET_SUCCESS, payload: {id}});
    }).catch(handleError);

const exportIndicatorsEpic = (action$, store, {ajax}) =>
  action$.ofType(EXPORT_INDICATORS)
    .mergeMap(() => {
      const indicators = getters.indicatorsInCurrentGraph(store.getState());
      const csv = 'IOC Type,Value\n' + indicators
        .map(x => `${x.get('type')},${x.get('value')}`)
        .join('\n');
      const filename = `GraphIOCs-${moment().format('YYYYMMDDHHmmss')}.csv`;
      saveTextToFile(filename, csv);
      return Observable.empty();
    })
    .catch(handleError);

/**
 * This epic is responsible for maintaining a valid state in constellation when
 * the user updates their whitelist. In practice this means that when the
 * whitelist changes:
 *
 * - Completely invalidate the graph cache
 * - Refetch the current graph, if any
 *
 * Invalidating the entire cache may seem like overkill, but it's a simple
 * solution to the issue. If the whitelist changes then it's possible every
 * graph in memory is stale. When the user whitelists a new ioc we could
 * identify any graphs within cache that contain that ioc and only operate on
 * them. However, in the case of the user removing a term from the whitelist
 * then by definition none of the graphs in memory will contain that ioc, so we
 * have no way of knowing which (if any) are stale.
 *
 * Currently, the downside of our approach is that node positioning and
 * undo/redo history are blown away since they are also part of the "cache". A
 * better approach would be to rearchitect this module and separate out
 * various parts of state. First and foremost would be node positioning, which
 * could live in it's own slice of state and be merged with nodes before getting
 * to the component layer.
 */
const whitelistSyncEpic = (action$, store) =>
  action$.ofType(SAVE_WHITELIST_ENTRY_SUCCESS, DELETE_WHITELIST_ENTRY_SUCCESS, SAVE_WHITELIST_ENTRIES_SUCCESS) // TODO: Delete Whitelisted IOC
    .filter(action => {
      // filter by special meta type that indicates whitelisting occured in graph view
      return action.meta && action.meta.invalidate_cache;
    })
    .map(prop('type'))
    .mergeMap(type => {
      const actions = [{type: INVALIDATE_GRAPH_CACHE}];

      // When whitelisting something it is done from the graph UI, so we want to
      // trigger an immediate refresh and re-fetch the graph. However, when
      // removing a term from the whitelist it is done from the settings UI.
      // This means we don't need to do a fetch as it will be done by the Graph
      // UI when the user returns to it (if they even do)
      //
      // NOTE: I'm unshifting to get the fetch in there before the invalidation.
      // Fetch triggers the loading state so the user won't get any interim
      // state while the cache has been wiped and the new graph not yet fetched
      if (type === SAVE_WHITELIST_ENTRIES_SUCCESS || type === SAVE_WHITELIST_ENTRY_SUCCESS) {
        const currentGraph = getters.currentGraph(store.getState());
        if (currentGraph.type === 'INDICATOR') {
          actions.unshift(fetch({
            id: currentGraph.id,
            type: 'INDICATOR',
            indicatorType: currentGraph.indicatorType,
            value: currentGraph.value
          }));
        } else {
          actions.unshift(fetch({
            id: currentGraph.id,
            type: 'RECORD',
          }));
        }
      }

      return actions;
    });

/**
 * Given an EditorState return the current ContentState. Very simple, but we end
 * up doing this quite a bit so I moved it into its own function.
 */
const getCurrentContent = editorState => editorState.getCurrentContent();

/**
 * NOTE: This isomorphism converts between a full draft-js editor state and
 * plain text stringified JSON to be stored server side. As you will see, the
 * from and to methods simply reverse eachother.
 */
const NoteIso = {
  to: pipe(
    getCurrentContent,
    convertToRaw,
    JSON.stringify
  ),
  from: pipe(
    JSON.parse,
    convertFromRaw,
    EditorState.createWithContent,
  ),
};

const fromNoteResponse = (data: RawNoteResponse) => {
  const result = Note(data).set('editorState', NoteIso.from(data.note));
  return result;
};

const annotationsFromGraphFetch = pipe(
  keyPath(['payload', 'data', 'nodes']),
  filter(x => Array.isArray(x.annotations)),
  map(x => [x.id, x.annotations]), // Create pairs mapping IDs to annotations array
);

const processAnnotations = pipe(
  fromPairs,
  map(pipe( // Turn each array of annotations into an array of OrderedMaps
    map(fromNoteResponse),
    listToOrderedMap('guid'),
  )),
  Map, // Convert to map
);

/**
 * NOTE: Unfortunately we are currently doing some immutable data conversion in
 * the analysis epics before dispatching. In order to interop smoothly with our
 * existing data parsing logic here in constellation we need plain JS objects
 * and arrays to pass in for processing. This epic just converts the data
 * payload back into plain JS before sending it through to the processNotesEpic
 *
 * Also note that the mapped data is not an actual action. It lacks a type. It
 * just formats the data to be JS and puts it at the path payload.data which
 * annotationsFromGraphFetch can pick up and handle.
 */
const getFetchedCasesStream = action$ =>
  action$.ofType(FETCH_ANALYSIS_SUCCESS)
    .mergeMap(action => {
      return Observable.of(action)
        .map(x => ({
          payload: {
            data: x.payload.data.toJS(), // See NOTE
          },
        }))
        .catch(err => {
          console.warn('Could not process annotations for analysis', err);
          return Observable.empty();
        });
    });

/**
 * Whenever the graph is fetched grab all the annotations from all nodes and
 * prepare them to be stored in the notes store, with a mapping of nodeId ->
 * OrderedMap of note items
 *
 * NOTE: This is how notes are "fetched". There is no explicit fetching.
 */
const processNotesEpic = (action$) =>
  Observable.merge(
    action$.ofType(FETCH_SUCCESS, FETCH_EXPLOSION_SUCCESS),
    getFetchedCasesStream(action$) // See NOTE above getFetchedCasesStream
  )
    .mergeMap(x =>
      Observable.of(x) // Wrap to facilitate error catching
        .map(annotationsFromGraphFetch)
        .filter(x => x.length) // No further processing unless we have annotations
        .map(processAnnotations)
        .map(payload => ({
          type: PROCESS_ANNOTATIONS_SUCCESS,
          payload,
        }))
        .catch(err => Observable.of({
          type: PROCESS_ANNOTATIONS_FAILURE,
          error: true,
          payload: err,
        })));

/**
 * Fetch annotations for a single report when the report is fetched
 *
 * NOTE: We could probably do this with the store_report action rather than the
 * success action, assuming that it would be successful.
 */
const fetchAnnotationsEpic = (action$, store, {ajax}) =>
  action$.ofType(STORE_REPORT_SUCCESS)
    .map(prop('payload'))
    .mergeMap(x => {
      const initialId = x.get('id');
      const url = querystring({initialId}, `${graphEndpoint()}/annotations`);
      return ajax.get(url)
        .map(prop('response'))
        .map(res => [[initialId, res]]) // Format data for processAnnotations
        .map(processAnnotations)
        .filter(x => {
          // NOTE: If there were no annotations don't dispatch anything. This
          // makes sure a node doesn't accidentally display a "has notes" icon
          // on the graph simply because its id was put into the store
          return x.first().size > 0;
        })
        .map(payload => ({
          type: PROCESS_ANNOTATIONS_SUCCESS,
          payload,
        }))
        .catch(err => Observable.of({
          type: PROCESS_ANNOTATIONS_FAILURE,
          error: true,
          payload: err,
        }));
    });


/**
 * NOTE: The findEntityRanges API is very strange (IMO). It takes a callback
 * which will be called with the range of the found entity. I.e. [number,
 * number] tuples. Two things:
 *
 * - why use a callback for a sync operation?
 * - what if, like me, you actually want the entities and not the ranges?
 *
 * This function solves those problems.
 */
const getMentionEntities = contentState => {
  const results = [];
  const noop = () => {
  };

  contentState.getBlocksAsArray().forEach(block => {
    block.findEntityRanges(x => {
      const k = x.getEntity();
      const ent = k ? contentState.getEntity(k) : null;

      if (ent) {
        results.push(ent);
      }

      return false; // See NOTE
    }, noop);
  });

  return results;
};

const getMentions = pipe(
  getCurrentContent,
  getMentionEntities,
  map(keyPath(['data', 'mention']))
);

/**
 * NOTE: When loaded into a note for editing the mentions are not immutable,
 * therefore we need to use the get helper to ensure that getting the GUID
 * works. However, this is not ideal. We should not have multiple data sources
 * here. Deeper investigation will likely reveal what's happening and why the
 * data isn't always mapped to immutable.
 */
const getMentionGuids = pipe(
  getMentions,
  tap(x => {
    // See NOTE
    if (!Map.isMap(x)) {
      console.warn('Warning: Mentions are not immutable. Falling back to plain object getter.');
    }
  }),
  map(x => x.guid || x.get('guid')),
  uniq // Remove duplicates. People can be tagged more than once
);

const getPlainText = pipe(
  getCurrentContent,
  x => x.getPlainText(),
);

const updateNoteEpic = (action$, store, {ajax}) =>
  action$
    .ofType(UPDATE_NOTE)
    .map(prop('payload'))
    .mergeMap(({id, initialId, editorState}) =>
      Observable.defer(() => {
        return ajax
          .putJSON(`${graphEndpoint()}/annotations/${id}`, {
            initialId,
            note: NoteIso.to(editorState),
            mentions: getMentionGuids(editorState),
            plainText: getPlainText(editorState),
          })
          .map(prop('response'))
          .map(fromNoteResponse)
          .map(payload => ({
            type: UPDATE_NOTE_SUCCESS,
            payload,
          }));
      }).catch(err =>
        Observable.of({
          type: UPDATE_NOTE_FAILURE,
          error: true,
          payload: err,
        })
      )
    );

const saveNoteEpic = (action$, store, {ajax}) =>
  action$.ofType(SAVE_NOTE)
    .map(prop('payload'))
    .mergeMap(({id, editorState}) => Observable.defer(() => {
      return ajax.postJSON(`${graphEndpoint()}/annotations/add`, {
        initialId: id,
        note: NoteIso.to(editorState),
        mentions: getMentionGuids(editorState),
        plainText: getPlainText(editorState),
      })
        .map(prop('response'))
        .map(fromNoteResponse)
        .map(payload => ({
          type: SAVE_NOTE_SUCCESS,
          payload,
        }))
        .catch(err => Observable.of({
          type: SAVE_NOTE_FAILURE,
          error: true,
          payload: err,
        }));
    }));

const deleteNoteEpic = (action$, store, {ajax}) =>
  action$.ofType(DELETE_NOTE)
    .map(prop('payload'))
    .mergeMap(({guid, nodeGuid}) => Observable.defer(() => {
      return ajax.delete(`${graphEndpoint()}/annotations/${guid}`)
        .mapTo({
          type: DELETE_NOTE_SUCCESS,
          payload: {guid, nodeGuid},
          meta: {
            alert: {type: 'success', text: 'Note removed.'}
          }
        })
        .catch(err => Observable.of({
          type: DELETE_NOTE_FAILURE,
          error: true,
          payload: err,
        }));
    }));

const notesUrlBase = /^\/constellation\/reports/g;
const getNoteFromLocation = keyPath(['query', 'note']);

/**
 * Handle deep linking to a note. This involves checking the URL for a note UUID
 * ans setting state accordingly.
 */
const noteUrlHandlerEpic = (action$) =>
  action$.ofType(LOCATION_CHANGE)
    .map(prop('payload'))
    .filter(x => {
      return notesUrlBase.test(x.pathname) && getNoteFromLocation(x);
    })
    .map(getNoteFromLocation)
    .switchMap(noteId => {
      return Observable.of({type: SET_ACTIVE_NOTE, payload: noteId});
    });


const selectCurrentReportEpic = (action$) =>
  action$.ofType(REPLACE_SELECTION, GO_TO_BREADCRUMB)
    .map(prop('payload'))
    .mergeMap(id => {

      return Observable.of({
        type: SET_CURRENT_REPORT_ID,
        payload: id
      },
        {type: RESET_HIGHLIGHT_NODE, payload: id}
        );
    });


const goToBreadcrumbEpic = (action$) =>
  action$.ofType(GO_TO_BREADCRUMB)
    .map(prop('payload'))
    .mergeMap(id => {
      return Observable.of({
        type: REPLACE_SELECTION,
        payload: Array(id),
      });
    });

const formatNodeClickToBCrumb = (node) => {
  const {id, guid, nodeType, title, value, name, indicatorType, source} = node;

  switch (nodeType) {
    case 'INDICATOR':
      return {
        id: guid,
        nodeType,
        title: value,
        indicatorType,
        source,
      };
    case 'INDICATOR_TAG':
      return {
        id: guid,
        nodeType: nodeType.replace(/_/g, ' '), // Humanize INDICATOR_TAG
        title: name,
        source,
      };
    default: // Default is reports
      return {
        id,
        title,
        nodeType,
        source,
      };
  }
};
const updateBreadcrumbEpic = (action$) =>
  action$.ofType(NODE_CLICKED)
    .map(prop('payload'))
    .mergeMap(node => {

      return Observable.of({
        type: ADD_BREADCRUMB,
        payload: formatNodeClickToBCrumb(node)
      });
    });

const initBreadcrumbEpic = (action$) =>
  action$.ofType(FETCH_SUCCESS)
    .map(prop('payload'))
    .map(prop('data'))
    .map(prop('nodes'))
    .mergeMap(graphNodes => {
      const rootNode = graphNodes[0];
      return rootNode ?
      Observable.of({
        type: ADD_BREADCRUMB,
          payload: formatNodeClickToBCrumb(rootNode)
       })
        :
      Observable.empty();
    });

export default combineEpics(
  fetchEpic,
  fetchExplosionEpic,
  deleteCurrentSelectionEpic,
  toggleActiveFilterEpic,
  cacheGraphSimulationEpic,
  redirectOnRemoveEpic,
  undoEpic,
  redoEpic,
  resetEpic,
  exportIndicatorsEpic,
  whitelistSyncEpic,
  updateNoteEpic,
  saveNoteEpic,
  processNotesEpic,
  deleteNoteEpic,
  fetchAnnotationsEpic,
  noteUrlHandlerEpic,
  selectCurrentReportEpic,
  updateBreadcrumbEpic,
  goToBreadcrumbEpic,
  initBreadcrumbEpic,
);



// WEBPACK FOOTER //
// ./src/app/modules/constellation/epics.js